import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBFreelancerReview } from "@/lib/db-types";

export interface FreelancerReviewRow extends DBFreelancerReview {
  author: { full_name: string } | null;
  project: { name: string; project_code: string | null } | null;
}

/** VIB-2925: vurderinger per frilanser, nyeste først. Få rader per frilanser, så plain select. */
export function useFreelancerReviews(freelancerId: string | undefined) {
  return useQuery({
    queryKey: ["freelancer-reviews", freelancerId],
    enabled: !!freelancerId,
    queryFn: async (): Promise<FreelancerReviewRow[]> => {
      const { data, error } = await supabase
        .from("freelancer_reviews")
        .select("*, author:users!freelancer_reviews_created_by_fkey(full_name), project:projects(name, project_code)")
        .eq("freelancer_id", freelancerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as FreelancerReviewRow[];
    },
  });
}

export interface ReviewInput {
  freelancer_id: string;
  rating: number;
  comment: string | null;
  project_id: string | null;
}

export function useSaveFreelancerReview() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: ReviewInput }) => {
      if (input.rating < 1 || input.rating > 5) throw new Error("Velg 1 til 5 stjerner");
      const fields = { rating: input.rating, comment: input.comment?.trim() || null, project_id: input.project_id };
      const { error } = id
        ? await supabase.from("freelancer_reviews").update(fields).eq("id", id)
        : await supabase.from("freelancer_reviews").insert({ ...fields, freelancer_id: input.freelancer_id, created_by: user?.id ?? null });
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_reviews", { freelancerId: vars.input.freelancer_id });
      toast.success("Vurderingen er lagret");
    },
    onError: (err: Error) => toast.error(`Kunne ikke lagre vurderingen: ${err.message}`),
  });
}

export function useDeleteFreelancerReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; freelancerId: string }) => {
      const { error } = await supabase.from("freelancer_reviews").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancer_reviews", { freelancerId: vars.freelancerId });
      toast.success("Vurderingen er slettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke slette vurderingen: ${err.message}`),
  });
}
